import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { toast } from 'react-toastify';
import './style.css';

// Global flag to prevent multiple initializations
let isGoogleOneTapInitialized = false;

/**
 * Google One-Tap Component
 * Implements Google Identity Services (GIS) for seamless authentication
 * 
 * Features:
 * - One-Tap automatic prompt
 * - Sign In With Google button
 * - No page redirects
 * - Better UX than OAuth redirect flow
 */
const GoogleOneTap = ({
    autoPrompt = false,          // Changed default to false
    autoSelect = false,          // Added autoSelect prop, default false
    showButton = true,            // Show "Sign in with Google" button
    buttonText = "Continue with Google",
    context = "signin",          // 'signin', 'signup', or 'use'
    onSuccess,                   // Optional callback on success
    onError                      // Optional callback on error
}) => {
    const { googleOneTapLogin, user } = useAuth();
    const { trackError } = useAnalytics();
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const hasInitialized = useRef(false);
    const buttonRef = useRef(null);
    const promptRef = useRef(null);
    const isProcessing = useRef(false);
    const abortControllerRef = useRef(null);
    const retryTimeoutRef = useRef(null);
    const promptTimeoutRef = useRef(null);
    const resizeObserverRef = useRef(null);

    // Get action from location state or localStorage
    const action = location.state?.action || localStorage.getItem('pending_oauth_action');

    // Clean up on unmount
    useEffect(() => {
        return () => {
            // Clear any retry timeouts
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
            }
            if (promptTimeoutRef.current) {
                clearTimeout(promptTimeoutRef.current);
            }
            // Cancel any ongoing prompts
            if (window.google?.accounts?.id) {
                try {
                    window.google.accounts.id.cancel();
                } catch (e) {
                    console.log('Error cancelling Google One-Tap prompt:', e);
                }
            }
            // Abort any ongoing requests
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            // Disconnect resize observer
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
            }
            isProcessing.current = false;
            hasInitialized.current = false;
            isGoogleOneTapInitialized = false; // Reset global flag on unmount
        };
    }, []);

    // Handle credential response from Google
    const handleCredentialResponse = async (response) => {
        console.log('Google One-Tap response received:', response);

        // Prevent multiple simultaneous requests
        if (isProcessing.current || isLoading) {
            console.log('One-Tap request already in progress, skipping...');
            return;
        }

        isProcessing.current = true;
        setIsLoading(true);

        try {
            // The response contains a JWT ID token
            const idToken = response.credential;
            console.log('Google ID Token received:', idToken ? 'YES' : 'NO');

            if (!idToken) {
                throw new Error('No ID token received from Google');
            }

            // Create abort controller for this request
            abortControllerRef.current = new AbortController();

            // Send to backend for verification and login
            console.log('Sending ID token to backend for verification...');
            const result = await googleOneTapLogin(idToken);
            console.log('Successfully authenticated with backend');

            // Track Google authentication
            // NOTE: Tracking is now handled by the backend to avoid double-counting.
            // The backend tracks "signup_completed" or "login_completed" with method: "google_one_tap"

            toast.success('Successfully signed in with Google!');

            // Determine redirect path
            let redirectPath = '/dashboard';
            if (action === 'level-test') {
                redirectPath = '/level-test';
            } else if (action === 'demo') {
                redirectPath = '/demo-confirmation';
            } else if (action === 'subscribe') {
                redirectPath = '/subscribe';
            }

            // Clear action from localStorage
            localStorage.removeItem('pending_oauth_action');

            // Call success callback if provided
            if (onSuccess) {
                onSuccess(redirectPath);
            } else {
                navigate(redirectPath);
            }
        } catch (err) {
            console.error('Google One-Tap error:', err);
            const errorMessage = err.response?.data?.error?.message || 'Failed to sign in with Google';

            // Track error
            trackError('google_auth_failed', errorMessage, {
                auth_method: 'google_one_tap',
                context: context,
            });

            toast.error(errorMessage);

            // Call error callback if provided
            if (onError) {
                onError(err);
            }
        } finally {
            isProcessing.current = false;
            setIsLoading(false);
            abortControllerRef.current = null;
        }
    };

    // Apply custom styling to ensure proper button sizing
    const applyButtonStyling = () => {
        const buttonContainer = buttonRef.current;
        if (!buttonContainer) return;

        // Apply styles to the container
        buttonContainer.style.display = 'flex';
        buttonContainer.style.justifyContent = 'center';
        buttonContainer.style.alignItems = 'center';
        buttonContainer.style.width = '100%';
        buttonContainer.style.minHeight = '50px';
        buttonContainer.style.margin = '0 auto';
        buttonContainer.style.maxWidth = '420px';

        // Find and style the Google button wrapper
        const wrapperDiv = buttonContainer.querySelector('.S9gUrf-YoZ4jf');
        if (wrapperDiv) {
            wrapperDiv.style.width = '100%';
            wrapperDiv.style.maxWidth = '420px';
            wrapperDiv.style.position = 'relative';
            wrapperDiv.style.minWidth = '200px';
        }

        // Find and style the iframe
        const iframe = buttonContainer.querySelector('iframe');
        if (iframe) {
            iframe.style.width = '100%';
            iframe.style.height = '50px';
            iframe.style.minHeight = '50px';
            iframe.style.maxWidth = '420px';
            iframe.style.minWidth = '200px';
            iframe.style.border = '0';
            iframe.style.margin = '0';
        }

        // Also style any parent divs that might constrain the size
        const parentDivs = buttonContainer.querySelectorAll('div');
        parentDivs.forEach(div => {
            if (div.style.position === 'relative') {
                div.style.width = '100%';
                div.style.maxWidth = '420px';
            }
        });
    };

    // Create a MutationObserver to watch for Google's attempts to resize the button
    const createResizeObserver = () => {
        if (!buttonRef.current) return;

        const observer = new MutationObserver(() => {
            // Reapply styles whenever Google tries to change them
            applyButtonStyling();
        });

        observer.observe(buttonRef.current, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'width', 'height']
        });

        return observer;
    };

    // Show One-Tap prompt with cooldown logic
    const showOneTapPrompt = () => {
        // Check for cooldown
        const cooldownStr = localStorage.getItem('google_one_tap_cooldown');
        if (cooldownStr) {
            const cooldownTime = parseInt(cooldownStr, 10);
            const now = Date.now();
            // 7 days cooldown (7 * 24 * 60 * 60 * 1000)
            if (now < cooldownTime + (7 * 24 * 60 * 60 * 1000)) {
                console.log('Google One-Tap is in cooldown period (7 days)');
                return;
            }
        }

        // Cancel any existing prompt to prevent the "only one request" error
        if (window.google?.accounts?.id) {
            try {
                window.google.accounts.id.cancel();
            } catch (e) {
                console.log('Error cancelling existing prompt:', e);
            }
        }

        // Check if document is visible and focused
        if (document.hidden || document.visibilityState !== 'visible') {
            console.log('Document not visible, skipping One-Tap prompt');
            return;
        }

        // Check if user is already focused on an input field
        if (document.activeElement &&
            (document.activeElement.tagName === 'INPUT' ||
                document.activeElement.tagName === 'TEXTAREA' ||
                document.activeElement.tagName === 'SELECT')) {
            console.log('User is focused on input field, skipping One-Tap prompt');
            return;
        }

        window.google.accounts.id.prompt((notification) => {
            promptRef.current = notification;

            if (notification.isNotDisplayed()) {
                const reason = notification.getNotDisplayedReason();
                console.log('One-Tap not displayed:', reason);

                if (reason === 'suppressed_by_user') {
                    // Start cooldown if user specifically suppressed it
                    localStorage.setItem('google_one_tap_cooldown', Date.now().toString());
                }
            } else if (notification.isSkippedMoment()) {
                const reason = notification.getSkippedReason();
                console.log('One-Tap skipped:', reason);

                // Set cooldown if user actively closed/skipped it
                if (reason === 'user_cancel' || reason === 'tap_outside') {
                    localStorage.setItem('google_one_tap_cooldown', Date.now().toString());
                    console.log('One-Tap cooldown activated for 24h');
                }
            } else if (notification.isDismissedMoment()) {
                const reason = notification.getDismissedReason();
                console.log('One-Tap dismissed:', reason);

                // Set cooldown for active dismissals
                if (reason === 'credential_returned') {
                    // Success, no cooldown needed (redirects anyway)
                } else {
                    localStorage.setItem('google_one_tap_cooldown', Date.now().toString());
                    console.log('One-Tap cooldown activated for 24h');
                }
            }
        });
    };

    // Initialize Google Identity Services
    const initializeGoogleOneTap = async () => {
        const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

        if (!clientId) {
            console.error('Google Client ID not configured');
            toast.error('Google Sign-In is not configured properly');
            return;
        }

        console.log('Initializing Google One-Tap with Client ID:', clientId);

        try {
            // Load Google Identity Services script if not already loaded
            if (!window.google?.accounts?.id) {
                console.log('Loading Google Identity Services script...');
                await new Promise((resolve, reject) => {
                    const script = document.createElement('script');
                    script.src = 'https://accounts.google.com/gsi/client';
                    script.async = true;
                    script.defer = true;
                    script.onload = () => {
                        console.log('Google Identity Services script loaded successfully');
                        resolve();
                    };
                    script.onerror = (error) => {
                        console.error('Failed to load Google Identity Services script:', error);
                        reject(new Error('Failed to load Google Identity Services'));
                    };
                    document.head.appendChild(script);
                });
            }

            // Wait a bit more for the script to be fully ready
            await new Promise(resolve => setTimeout(resolve, 100));

            // Initialize Google Identity Services
            console.log('Initializing Google Identity Services...');
            window.google.accounts.id.initialize({
                client_id: clientId,
                callback: handleCredentialResponse,
                auto_select: autoSelect, // Use the new prop
                cancel_on_tap_outside: false, // Don't cancel when tapping outside
                context: context, // 'signin', 'signup', or 'use'
                // Add additional configuration for better One-Tap compatibility
                itp_support: true, // Enable ITP support for Safari
                nonce: '', // Optional nonce for additional security
            });
            console.log('Google Identity Services initialized successfully');

            // Show One-Tap prompt if enabled
            if (autoPrompt) {
                // Wait for page to be fully loaded and visible
                const showPrompt = () => {
                    console.log('Scheduling One-Tap prompt to show in 1500ms...');
                    promptTimeoutRef.current = setTimeout(() => {
                        showOneTapPrompt();
                    }, 1500);
                };

                // Check if document is already loaded
                if (document.readyState === 'loading') {
                    document.addEventListener('DOMContentLoaded', showPrompt);
                } else {
                    // Additional check for page visibility
                    if (document.hidden || document.visibilityState !== 'visible') {
                        const visibilityHandler = () => {
                            if (!document.hidden) {
                                document.removeEventListener('visibilitychange', visibilityHandler);
                                showPrompt();
                            }
                        };
                        document.addEventListener('visibilitychange', visibilityHandler);
                    } else {
                        showPrompt();
                    }
                }
            }

            // Render "Sign in with Google" button if enabled
            if (showButton && buttonRef.current) {
                console.log('Rendering Google Sign-In button...');
                try {
                    window.google.accounts.id.renderButton(
                        buttonRef.current,
                        {
                            type: 'standard',       // 'standard' or 'icon'
                            theme: 'outline',       // 'outline', 'filled_blue', or 'filled_black'
                            size: 'large',          // 'large', 'medium', or 'small'
                            text: buttonText === 'Continue with Google' ? 'continue_with' : 'signin_with',
                            shape: 'rectangular',   // 'rectangular', 'pill', 'circle', or 'square'
                            logo_alignment: 'left', // 'left' or 'center'
                            width: 420              // Increased width for better design match
                        }
                    );
                    console.log('Google Sign-In button rendered successfully');

                    // Apply additional styling immediately
                    setTimeout(applyButtonStyling, 50);
                    setTimeout(applyButtonStyling, 200);
                } catch (error) {
                    console.error('Failed to render Google Sign-In button:', error);
                    // Try to render a fallback button
                    buttonRef.current.innerHTML = `
                        <button style="
                            padding: 15px 20px;
                            border: 1px solid #ddd;
                            background: white;
                            color: #333;
                            font-size: 16px;
                            font-weight: 500;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            gap: 12px;
                            width: 100%;
                            cursor: pointer;
                            border-radius: 4px;
                            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                        ">
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M19.8055 10.2292C19.8055 9.55156 19.7501 8.86719 19.6323 8.19922H10.2002V12.0494H15.6014C15.3773 13.2911 14.6571 14.3898 13.6025 15.0879V17.5866H16.825C18.7172 15.8449 19.8055 13.2728 19.8055 10.2292Z" fill="#4285F4"/>
                                <path d="M10.2002 20.0006C12.9508 20.0006 15.2715 19.1151 16.8283 17.5865L13.6058 15.0879C12.7202 15.6979 11.5717 16.0433 10.2035 16.0433C7.5479 16.0433 5.28919 14.2834 4.51561 11.9097H1.19775V14.4842C2.79033 17.653 6.31363 20.0006 10.2002 20.0006Z" fill="#34A853"/>
                                <path d="M4.51234 11.9097C4.07154 10.668 4.07154 9.33687 4.51234 8.09521V5.52075H1.19776C-0.39483 8.69254 -0.39483 12.3124 1.19776 15.4842L4.51234 11.9097Z" fill="#FBBC04"/>
                                <path d="M10.2002 3.95805C11.6507 3.93593 13.0513 4.47262 14.1061 5.45722L16.976 2.60109C15.1854 0.904968 12.7419 -0.0287217 10.2002 0.000673888C6.31363 0.000673888 2.79033 2.34825 1.19775 5.52061L4.51233 8.09507C5.28264 5.71473 7.54461 3.95805 10.2002 3.95805Z" fill="#EA4335"/>
                            </svg>
                            ${buttonText}
                        </button>
                    `;

                    // Add click handler to the fallback button
                    const fallbackButton = buttonRef.current.querySelector('button');
                    if (fallbackButton) {
                        fallbackButton.addEventListener('click', () => {
                            // Trigger One-Tap prompt manually
                            if (window.google?.accounts?.id) {
                                window.google.accounts.id.prompt();
                            }
                        });
                    }

                    console.log('Fallback Google Sign-In button rendered successfully');
                }
            }

            // Mark as initialized
            hasInitialized.current = true;
            isGoogleOneTapInitialized = true;
        } catch (error) {
            console.error('Failed to initialize Google One-Tap:', error);
            toast.error('Failed to initialize Google Sign-In. Please try again.');
        }
    };

    useEffect(() => {
        // Skip if user is already logged in
        if (user) {
            return;
        }

        // Skip if already initialized globally or locally
        if (isGoogleOneTapInitialized || hasInitialized.current) {
            // Apply styling even if already initialized
            if (showButton) {
                // Use a timeout to ensure DOM is ready
                setTimeout(applyButtonStyling, 100);
                setTimeout(applyButtonStyling, 500); // Double-check after a longer delay
            }
            return;
        }

        // Initialize Google One-Tap
        initializeGoogleOneTap();

        // Cleanup
        return () => {
            // Clear any retry timeouts
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
            }
            if (promptTimeoutRef.current) {
                clearTimeout(promptTimeoutRef.current);
            }
            // Remove DOMContentLoaded listener if it exists
            document.removeEventListener('DOMContentLoaded', initializeGoogleOneTap);
        };
    }, [user, autoPrompt, showButton, buttonText, context, action, navigate, googleOneTapLogin, onSuccess, onError, isLoading]);

    // Apply styling when buttonRef changes
    useEffect(() => {
        if (showButton && buttonRef.current) {
            // Apply styling with a delay to ensure Google script has rendered the button
            const styleTimer = setTimeout(applyButtonStyling, 100);
            const styleTimer2 = setTimeout(applyButtonStyling, 500);

            // Set up mutation observer to maintain button sizing
            let mutationObserver = null;
            const observerTimer = setTimeout(() => {
                mutationObserver = createResizeObserver();
            }, 200);

            return () => {
                clearTimeout(styleTimer);
                clearTimeout(styleTimer2);
                clearTimeout(observerTimer);
                // Disconnect mutation observer
                if (mutationObserver) {
                    mutationObserver.disconnect();
                }
            };
        }
    }, [showButton, buttonRef.current]);

    // Don't render anything if user is already logged in
    if (user) {
        return null;
    }

    return (
        <div className="google-one-tap-container">
            {showButton && (
                <div
                    ref={buttonRef}
                    className="google-one-tap-button"
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '50px',
                        width: '100%',
                        margin: '0 auto',
                        maxWidth: '420px'
                    }}
                />
            )}
            {isLoading && (
                <div className="google-one-tap-loading">
                    <div className="loading-spinner"></div>
                    <div style={{ marginTop: '10px', textAlign: 'center', color: 'white' }}>Signing in with Google...</div>
                </div>
            )}
        </div>
    );
};

export default GoogleOneTap;